import React, { useState, useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Map from "../../components/common/Map";
import SaveGeometries from "../../components/areas/SaveGeometries";
import MapSelectComponent from "../../components/areas/MapSelectComponent";
import LocationSearchBar from "../../components/common/LocationSearchBar";
import { useMapData } from "../../hooks/useMapData";
import { useUrlState } from "../../hooks/useUrlState";
import { BoundingBox, mapsApi, MapGeometry, AvailableMap } from "../../services/mapsApi";
import { areaShapeLoader, fitView, outlineRings, readShape } from "../../geo/areaShapes";
import { DEMO_START_VIEW, IS_DEMO } from "../../services/demoApi";
import { COUNTRIES, DEMO_API_COUNTRY, areaCountry, mapCountries } from "../../config/countries";
import type { CountryCode, MapType } from "../../config/countries";
import { MODEL_GRID, catalogueCountry, catalogueZoomThreshold, isCatalogueArea, modelGridBand } from "../../config/catalogue";
import { WebMercatorViewport } from "@deck.gl/core";
import NavBar from "../../components/common/NavBar";

const SAMPLE_DATA: Array<{
  position: [number, number];
  population: number;
  name: string;
}> = [];

const ZOOM_THRESHOLDS: Record<string, number> = {
  admin_level_1: 8,
  admin_level_2: 9,
  admin_level_4: 11,
  grid_1000m: 11.6,
  grid_250m: 13,
};

/** True when one model-grid cell holds the other (catalogue cells carry their parents' keys). */
const overlapsCell = (a: MapGeometry, b: MapGeometry): boolean =>
  !!a.parents?.includes(b.geo_id) || !!b.parents?.includes(a.geo_id);

/** True when [lng, lat] lies inside a cell's outline (ray casting; grid cells are simple squares). */
const cellHolds = (g: MapGeometry, [x, y]: [number, number]): boolean =>
  outlineRings(readShape(g.geometry)!).some((ring) => {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  });

/** The zoom above which a layer loads: the old maps' table, else the catalogue's rule for its geo types. */
const zoomThreshold = (mapType: string): number => ZOOM_THRESHOLDS[mapType] ?? catalogueZoomThreshold(mapType);

const PolygonSelect: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Set once a save succeeds, so closing the dialog afterwards returns to the Areas section.
  const savedRef = useRef(false);
  const { filterState, addGridId, removeGridId } = useUrlState();
  const [selectedPoints, setSelectedPoints] = useState<typeof SAMPLE_DATA>([]);
  const [selectedGrids, setSelectedGrids] = useState<MapGeometry[]>([]);
  const [currentZoom, setCurrentZoom] = useState<number>(IS_DEMO ? DEMO_START_VIEW.zoom : 10);
  // A demo build opens on the demo country. Seed width/height (estimated from the window) so the
  // first geometry fetch can compute a bbox before the map emits a view change.
  const [viewState, setViewState] = useState<any>(
    IS_DEMO
      ? {
          ...DEMO_START_VIEW,
          pitch: 0,
          bearing: 0,
          width: typeof window !== "undefined" ? window.innerWidth - 320 : 1024,
          height: typeof window !== "undefined" ? window.innerHeight - 120 : 720,
        }
      : null
  );
  const [isSaveModalOpen, setIsSaveModalOpen] = useState<boolean>(false);
  const [selectedMapType, setSelectedMapType] = useState<string>(() => {
    const s = location.state as any;
    return s?.savedArea?.geo_type || s?.savedArea?.type || "";
  });
  const [editMode, setEditMode] = useState(false);
  const [savedArea, setSavedArea] = useState<any>(null);
  const [countryCode, setCountryCode] = useState<CountryCode>(() => {
    const s = location.state as any;
    const c = s?.savedArea ? areaCountry(s.savedArea) : null;
    return c ?? (IS_DEMO ? DEMO_API_COUNTRY : mapCountries()[0].code);
  });
  // An area made on the old maps is edited on them; everything else is made on the catalogue 1.0 maps where
  // the catalogue has the country (telcofy-demo#105).
  const [editingLegacy] = useState<boolean>(() => {
    const s = location.state as any;
    return !!s?.savedArea && !isCatalogueArea({ country_code: s.savedArea.country_code, type: s.savedArea.type });
  });
  const [catalogue, setCatalogue] = useState(false);
  // How the area's country is saved: the catalogue's own code marks an area made on its maps.
  const savedCountryCode = catalogue ? catalogueCountry(countryCode) : countryCode;
  const [availableMaps, setAvailableMaps] = useState<AvailableMap[]>([]);
  const [loadingMaps, setLoadingMaps] = useState(false);
  const { geometries, loading, error, fetchMapData, clearData, setGeometries } =
    useMapData();

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const interactionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastApiCenterRef = useRef<{
    longitude: number;
    latitude: number;
  } | null>(null);
  const loadedBoundingBoxRef = useRef<BoundingBox | null>(null);
  // The catalogue grid's rung follows the zoom: a zoom into another band reloads it even inside the loaded box.
  const loadedBandRef = useRef<number | null>(null);
  const zoomRef = useRef<number>(currentZoom);
  zoomRef.current = currentZoom;
  const isInteractingRef = useRef<boolean>(false);

  const onPointClick = useCallback((info: any) => {
    if (info.object) {
      const point = info.object;
      setSelectedPoints((prev) => {
        const exists = prev.find((p) => p.name === point.name);
        if (exists) {
          return prev.filter((p) => p.name !== point.name);
        }
        return [...prev, point];
      });
    }
  }, []);

  const onGridClick = useCallback(
    (info: any) => {
      console.log("Grid clicked:", info);
      if (info.object && info.object.id) {
        let clickedGridId = info.object.id;
        // A selected grid cell of another size is drawn over the cells on show: a click inside it means the cell
        // on show under the pointer, which then takes its place.
        const offRung =
          !geometries.some((g) => g.geo_id === clickedGridId) &&
          selectedGrids.find((g) => g.geo_id === clickedGridId)?.parents !== undefined;
        if (offRung && Array.isArray(info.coordinate)) {
          const under = geometries.find((g) => readShape(g.geometry) && cellHolds(g, info.coordinate));
          if (under) clickedGridId = under.geo_id;
        }

        let gridGeometry = geometries.find((g) => g.geo_id === clickedGridId);
        if (!gridGeometry) {
          gridGeometry = selectedGrids.find((g) => g.geo_id === clickedGridId);
        }

        if (gridGeometry) {
          const exists = selectedGrids.find((g) => g.geo_id === clickedGridId);
          // A grid cell replaces any selected cell that holds it or that it holds: the same ground counts once.
          const overlapping = selectedGrids.filter((g) => overlapsCell(g, gridGeometry!));
          if (exists) {
            setSelectedGrids((prev) =>
              prev.filter((g) => g.geo_id !== clickedGridId)
            );
            removeGridId(clickedGridId);
          } else if (selectedGrids.length - overlapping.length < 10) {
            overlapping.forEach((g) => removeGridId(g.geo_id));
            setSelectedGrids((prev) => [...prev.filter((g) => !overlapsCell(g, gridGeometry!)), gridGeometry!]);
            addGridId(clickedGridId);
          } else {
            alert("Maximum 10 grids can be selected for analysis");
          }
        }
      }
    },
    [geometries, selectedGrids, addGridId, removeGridId]
  );

  const handleGridRemove = useCallback(
    (gridId: string) => {
      setSelectedGrids((prev) => prev.filter((g) => g.geo_id !== gridId));
      removeGridId(gridId);
    },
    [removeGridId]
  );

  const handleGridsExport = useCallback(() => {
    setIsSaveModalOpen(true);
  }, []);

  const handleAddById = useCallback(
    async (ids: string[]) => {
      if (!selectedMapType || ids.length === 0) {
        return;
      }

      try {
        // Fetch geometries for the provided IDs
        const results = await areaShapeLoader.load({
          id: `ids:${savedCountryCode}:${selectedMapType}`,
          type: selectedMapType,
          ids,
          country_code: savedCountryCode,
        });

        if (results.length === 0) {
          alert('No geometries found for the provided ID(s)');
          return;
        }

        // Filter out IDs that are already selected
        const newGeometries = results.filter(
          (geom) => !selectedGrids.find((g) => g.geo_id === geom.geo_id)
        );

        if (newGeometries.length === 0) {
          alert('All provided IDs are already selected');
          return;
        }

        // Check if adding these would exceed the limit
        if (selectedGrids.length + newGeometries.length > 10) {
          alert(
            `Cannot add ${newGeometries.length} grid(s). Maximum 10 grids can be selected. Currently ${selectedGrids.length} selected.`
          );
          return;
        }

        // Add to selected grids, replacing any selected cell that holds an added one or that it holds
        selectedGrids
          .filter((g) => newGeometries.some((n) => overlapsCell(g, n)))
          .forEach((g) => removeGridId(g.geo_id));
        setSelectedGrids((prev) => [...prev.filter((g) => !newGeometries.some((n) => overlapsCell(g, n))), ...newGeometries]);

        // Add to geometries if not already present
        const existingIds = new Set(geometries.map((g) => g.geo_id));
        const toAdd = newGeometries.filter((g) => !existingIds.has(g.geo_id));
        if (toAdd.length > 0) {
          setGeometries([...geometries, ...toAdd]);
        }

        // Add to URL state
        newGeometries.forEach((geom) => {
          addGridId(geom.geo_id);
        });

        // Auto-zoom to the newly added geometries
        setViewState({
          ...fitView(newGeometries.map((g) => g.geometry), countryCode),
          pitch: 0,
          bearing: 0,
          transitionDuration: 1000,
        });

        console.log(`Successfully added ${newGeometries.length} geometries by ID`);
      } catch (error) {
        console.error('Failed to fetch geometries by ID:', error);
        throw error;
      }
    },
    [selectedMapType, selectedGrids, geometries, addGridId, removeGridId, setGeometries, countryCode, savedCountryCode]
  );

  const handleSaveReport = useCallback(
    (name: string, description: string) => {
      savedRef.current = true;
      console.log("Saving report:", {
        name,
        description,
        gridIds: filterState.gridIds,
      });
    },
    [filterState.gridIds]
  );

  const handleCloseSaveModal = useCallback(() => {
    setIsSaveModalOpen(false);
    if (savedRef.current) {
      savedRef.current = false;
      navigate("/?section=areas");
    }
  }, [navigate]);

  const handleMapTypeChange = useCallback(
    (mapTypeId: string) => {
      setSelectedMapType(mapTypeId);
      // Clear current selection when changing map types
      setSelectedGrids([]);
      // Clear all selected grid IDs from URL state
      filterState.gridIds.forEach(gridId => removeGridId(gridId));
      // Clear geometries when changing map types
      clearData();
      // Clear cached loaded area
      loadedBoundingBoxRef.current = null;
      lastApiCenterRef.current = null;
      console.log("Map type changed to:", mapTypeId);
    },
    [clearData, filterState.gridIds, removeGridId]
  );

  const handleLocationSearch = useCallback((lat: number, lng: number) => {
    setViewState((prevState: any) => ({
      ...prevState,
      longitude: lng,
      latitude: lat,
      zoom: 12.5,
      transitionDuration: 1000,
    }));
  }, []);

  const isViewWithinLoadedArea = useCallback(
    (currentBoundingBox: BoundingBox) => {
      if (!loadedBoundingBoxRef.current) {
        return false;
      }

      const loaded = loadedBoundingBoxRef.current;
      const current = currentBoundingBox;

      // Check if current view is entirely within the loaded bounding box with some margin
      const margin = 0.01; // Add small margin to prevent edge reloads
      return (
        current.lng1 >= loaded.lng1 - margin &&
        current.lng2 <= loaded.lng2 + margin &&
        current.lat1 >= loaded.lat1 - margin &&
        current.lat2 <= loaded.lat2 + margin
      );
    },
    []
  );

  const onViewStateChange = useCallback((newViewState: any) => {
    setCurrentZoom(newViewState.zoom);
    setViewState(newViewState);

    isInteractingRef.current = true;

    if (interactionTimeoutRef.current) {
      clearTimeout(interactionTimeoutRef.current);
    }

    interactionTimeoutRef.current = setTimeout(() => {
      isInteractingRef.current = false;
    }, 300);
  }, []);

  const getBoundingBox = useCallback(() => {
    if (!viewState || !viewState.width || !viewState.height) {
      return null;
    }

    const viewport = new WebMercatorViewport(viewState);

    const [west, south] = viewport.unproject([0, viewState.height]);
    const [east, north] = viewport.unproject([viewState.width, 0]);

    return {
      lng1: west,
      lat1: south,
      lng2: east,
      lat2: north,
    };
  }, [viewState]);

  useEffect(() => {
    let cancelled = false;

    const fetchAvailableMaps = async () => {
      setLoadingMaps(true);
      try {
        const response = await mapsApi.getAvailableMaps(countryCode, editingLegacy);
        if (!cancelled) {
          setCatalogue(response.catalogue === true);
          setAvailableMaps(response.results ?? []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch available maps:", error);
          setCatalogue(false);
          setAvailableMaps([]);
        }
      } finally {
        if (!cancelled) setLoadingMaps(false);
      }
    };

    fetchAvailableMaps();
    return () => { cancelled = true; };
  }, [countryCode, editingLegacy]);

  const handleCountryChange = useCallback(
    (code: CountryCode) => {
      setCountryCode(code);
      setSelectedMapType("");
      setSelectedGrids([]);
      filterState.gridIds.forEach((id) => removeGridId(id));
      clearData();
      loadedBoundingBoxRef.current = null;
      lastApiCenterRef.current = null;
      const view = COUNTRIES[code].defaultView;
      setViewState((prev: any) => ({
        ...prev,
        ...view,
        pitch: 0,
        bearing: 0,
        transitionDuration: 1000,
      }));
    },
    [clearData, filterState.gridIds, removeGridId]
  );

  useEffect(() => {
    let zoomTimer: ReturnType<typeof setTimeout> | undefined;

    const state = location.state as {
      editMode?: boolean;
      savedArea?: any;
      geometries?: MapGeometry[];
    } | null;
    console.log("PolygonSelect received state:", state);
    if (state?.savedArea && state?.geometries) {
      console.log("Processing saved area:", state.savedArea);
      console.log("Processing geometries:", state.geometries);

      if (state.editMode) {
        setEditMode(true);
        setSavedArea(state.savedArea);
      }

      // Set country from the saved area
      const savedCountry = areaCountry(state.savedArea);
      if (savedCountry) {
        setCountryCode(savedCountry);
      }

      // Set the map type from the saved area
      console.log("Setting map type to:", state.savedArea.geo_type || state.savedArea.type);
      setSelectedMapType(state.savedArea.geo_type || state.savedArea.type);

      const transformedGeometries = state.geometries.map((g: any) => ({
        geo_id: String(g.geo_id || g.grid_id),
        geometry: g.geometry,
        ...(g.name != null ? { name: g.name } : {}),
        ...(typeof g.size_m === 'number' ? { size_m: g.size_m } : {}),
        ...(Array.isArray(g.parents) ? { parents: g.parents } : {}),
      }));

      const savedAreaGrids = transformedGeometries.filter(
        (g) =>
          state.savedArea.ids.includes(Number(g.geo_id)) ||
          state.savedArea.ids.includes(g.geo_id)
      );
      console.log("Edit: matched", savedAreaGrids.length, "grids from", transformedGeometries.length, "geometries");

      setSelectedGrids(savedAreaGrids);

      savedAreaGrids.forEach((grid) => {
        addGridId(grid.geo_id);
      });

      setGeometries(transformedGeometries);

      // Zoom to the saved area.  Deferred so it runs after DeckGL's controller
      // has initialised (which fires onViewStateChange with the default Oslo
      // position).  Setting currentZoom immediately prevents the zoom-check
      // effect from calling clearData() before the map has moved.
      if (savedAreaGrids.length > 0) {
        const framed = fitView(savedAreaGrids.map((g) => g.geometry), savedCountry);
        setCurrentZoom(framed.zoom);
        zoomTimer = setTimeout(() => {
          setViewState({
            ...framed,
            pitch: 0,
            bearing: 0,
            transitionDuration: 1000,
          });
        }, 150);
      }
    }

    return () => { if (zoomTimer) clearTimeout(zoomTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const debouncedFetchMapData = useCallback(
    (
      boundingBox: BoundingBox,
      center: { longitude: number; latitude: number }
    ) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        if (
          !isInteractingRef.current &&
          selectedMapType &&
          (catalogue || [
            "grid_250m",
            "grid_1000m",
            "admin_level_2",
            "admin_level_4",
          ].includes(selectedMapType))
        ) {
          // Expand bounding box by 50% to load more area for future panning
          const lngRange = boundingBox.lng2 - boundingBox.lng1;
          const latRange = boundingBox.lat2 - boundingBox.lat1;
          const expandedBoundingBox = {
            lng1: boundingBox.lng1 - lngRange * 0.25,
            lat1: boundingBox.lat1 - latRange * 0.25,
            lng2: boundingBox.lng2 + lngRange * 0.25,
            lat2: boundingBox.lat2 + latRange * 0.25,
          };

          fetchMapData(expandedBoundingBox, selectedMapType as MapType, countryCode, catalogue, zoomRef.current);
          lastApiCenterRef.current = center;
          loadedBoundingBoxRef.current = expandedBoundingBox;
          loadedBandRef.current = modelGridBand(zoomRef.current);
        }
      }, 500);
    },
    [fetchMapData, selectedMapType, countryCode, catalogue]
  );

  useEffect(() => {
    const threshold = zoomThreshold(selectedMapType);

    if (currentZoom > threshold && viewState) {
      const boundingBox = getBoundingBox();

      if (boundingBox) {
        const center = {
          longitude: viewState.longitude,
          latitude: viewState.latitude,
        };

        // Only fetch if current view is outside the loaded area, or the grid's rung changed with the zoom
        const bandChanged =
          catalogue && selectedMapType === MODEL_GRID && loadedBandRef.current !== modelGridBand(currentZoom);
        if (bandChanged || !isViewWithinLoadedArea(boundingBox as BoundingBox)) {
          debouncedFetchMapData(boundingBox as BoundingBox, center);
        }
      }
    } else if (currentZoom <= threshold) {
      clearData();
      lastApiCenterRef.current = null;
      loadedBoundingBoxRef.current = null;
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      // The interaction timer is left to run: cancelling it here (as on opening the page with no layer picked)
      // left the map marked as moving, so picking a layer loaded nothing until the map was panned.
    }
  }, [
    currentZoom,
    viewState,
    selectedMapType,
    getBoundingBox,
    isViewWithinLoadedArea,
    debouncedFetchMapData,
    clearData,
    catalogue,
  ]);

  useEffect(() => {
    if (filterState.gridIds.length === 0 && !location.state) {
      setSelectedGrids([]);
    }
  }, [filterState.gridIds, location.state]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="h-screen flex">
      <div className="flex-1 flex flex-col">
        <NavBar
          title={
            editMode
              ? `Edit: ${savedArea?.name || "Standard Polygons"}`
              : "Standard Polygons"
          }
          showBackButton={true}
        />

        <div className="flex-1 relative">
          <Map
            data={SAMPLE_DATA}
            selectedPoints={selectedPoints}
            onPointClick={onPointClick}
            onViewStateChange={onViewStateChange}
            viewState={viewState}
            geometries={geometries}
            loading={loading}
            selectedGrids={selectedGrids}
            onGridClick={onGridClick}
            initialView={COUNTRIES[countryCode].defaultView}
          />
          <LocationSearchBar
            onSearch={handleLocationSearch}
            country={countryCode}
            placeholder={`Search an address in ${COUNTRIES[countryCode].label}`}
          />
        </div>

        <div className="bg-surface border-t border-paper-300 px-4 py-2 flex-shrink-0">
          <div className="max-w-full px-4 sm:px-6 lg:px-8">
            <div className="text-sm text-ink-muted">
              <div className="flex items-center space-x-4">
                {(() => {
                  const threshold = zoomThreshold(selectedMapType);
                  return currentZoom <= threshold ? (
                    <div className="flex items-center space-x-2">
                      <span className="text-warn-ink font-medium">
                        📍 Zoom in to level {threshold}+ to view{" "}
                        {selectedMapType ? "areas" : "grid areas"}
                      </span>
                      <span className="text-ink-subtle text-sm">
                        (Current: {currentZoom.toFixed(1)})
                      </span>
                    </div>
                  ) : (
                    <span>
                      Current Zoom Level:{" "}
                      <span className="font-medium text-ink-soft">
                        {currentZoom.toFixed(2)}
                      </span>
                    </span>
                  );
                })()}
                {loading && (
                  <span className="text-xs text-note-ink">
                    Loading geometries...
                  </span>
                )}
                {error && (
                  <span className="text-xs text-bad-ink">Error: {error}</span>
                )}
              </div>
              {currentZoom > zoomThreshold(selectedMapType) &&
                getBoundingBox() && (
                  <div className="mt-1">
                    <span className="text-xs text-ink-subtle">
                      Bounding Box:{" "}
                    </span>
                    <span className="font-mono text-xs text-ink-soft">
                      lng1: {getBoundingBox()?.lng1.toFixed(6)}, lat1:{" "}
                      {getBoundingBox()?.lat1.toFixed(6)}, lng2:{" "}
                      {getBoundingBox()?.lng2.toFixed(6)}, lat2:{" "}
                      {getBoundingBox()?.lat2.toFixed(6)}
                    </span>
                  </div>
                )}
              {geometries.length > 0 && (
                <div className="mt-1">
                  <span className="text-xs text-ok-ink">
                    Loaded {geometries.length} geometries
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <MapSelectComponent
        selectedMapType={selectedMapType}
        onMapTypeChange={handleMapTypeChange}
        selectedGrids={selectedGrids}
        onGridRemove={handleGridRemove}
        onGridsExport={handleGridsExport}
        onAddById={handleAddById}
        availableMaps={availableMaps}
        loadingMaps={loadingMaps}
        countryCode={countryCode}
        onCountryChange={handleCountryChange}
      />

      <SaveGeometries
        isOpen={isSaveModalOpen}
        onClose={handleCloseSaveModal}
        selectedIds={filterState.gridIds}
        selectedMapType={selectedMapType}
        onSave={handleSaveReport}
        countryCode={savedCountryCode}
        editArea={
          editMode && savedArea
            ? { id: savedArea.id, name: savedArea.name, description: savedArea.description }
            : undefined
        }
      />
    </div>
  );
};

export default PolygonSelect;
